import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, User, Plus, MessageCircle, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import CityPicker from '@/components/CityPicker';
import { LANGUAGE_OPTIONS } from '@/data/languages';
import { COUNTRY_CODES } from '@/data/countryCodes';
import { CURRENCY_OPTIONS, PRICE_AMOUNTS } from '@/data/pricing';
import { SPECIALTY_OPTIONS } from '@/data/specialties';
import { resizeImageToDataUrl } from '@/data/image';
import NotificationBell from '@/components/NotificationBell';

const AstrologerDashboard = () => {
  const { user, logout, checkAuth, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({
    display_name: '',
    bio: '',
    region: '',
    online_service: true,
    languages: [],
    phone_country_code: '+852',
    phone_number: '',
    price_currency: 'HK$',
    price_min: '',
    price_max: '',
    avatar: '',
  });
  const [specialtiesSelected, setSpecialtiesSelected] = useState([]);
  const [specialtiesCustom, setSpecialtiesCustom] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchOrders();
    fetchAnswered();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchAnswered = async () => {
    try {
      // Lightweight count — don't pull the whole answered-requests payload for a badge.
      const response = await axios.get(`${API}/astrologer/answered-count`, { withCredentials: true });
      setAnsweredCount(response.data?.count || 0);
    } catch (error) {
      console.error('Error fetching answered:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/astrologer/profile`, { withCredentials: true });
      if (response.data) {
        setProfile(response.data);
      } else {
        setShowProfileDialog(true);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = (lang) => {
    setProfileData((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const toggleSpecialty = (s) => {
    setSpecialtiesSelected((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('請選擇圖片檔'); return; }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 256);
      setProfileData((p) => ({ ...p, avatar: dataUrl }));
    } catch (err) {
      toast.error('圖片處理失敗');
    }
  };

  const openEdit = () => {
    if (!profile) return;
    const m = (profile.price_range || '').match(/^(\S+)\s+([\d,]+)\s*-\s*([\d,]+)/);
    const cur = m && CURRENCY_OPTIONS.some((c) => c.value === m[1]) ? m[1] : 'HK$';
    setProfileData({
      display_name: profile.display_name || '',
      bio: profile.bio || '',
      region: profile.region || '',
      online_service: profile.online_service ?? true,
      languages: profile.languages || [],
      phone_country_code: profile.phone_country_code || '+852',
      phone_number: profile.phone_number || '',
      price_currency: cur,
      price_min: m ? m[2].replace(/,/g, '') : '',
      price_max: m ? m[3].replace(/,/g, '') : '',
      avatar: profile.avatar || '',
    });
    const presetSet = new Set(SPECIALTY_OPTIONS);
    const specs = profile.specialties || [];
    setSpecialtiesSelected(specs.filter((s) => presetSet.has(s)));
    setSpecialtiesCustom(specs.filter((s) => !presetSet.has(s)).join('、'));
    setEditing(true);
    setShowProfileDialog(true);
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();

    const customSpecs = specialtiesCustom.split(/[,，、]/).map((s) => s.trim()).filter(Boolean);
    const specialties = [...new Set([...specialtiesSelected, ...customSpecs])];
    if (!specialties.length) {
      toast.error('請選擇至少一項專長');
      return;
    }
    if (!profileData.region.trim()) {
      toast.error('請選擇或輸入地區');
      return;
    }
    if (!profileData.price_min || !profileData.price_max) {
      toast.error('請選擇價格上下限');
      return;
    }
    const min = Number(profileData.price_min);
    const max = Number(profileData.price_max);
    if (min >= max) {
      toast.error('價格上限須大於下限');
      return;
    }
    if (!profileData.languages.length) {
      toast.error('請選擇至少一種語言');
      return;
    }
    if (!editing && !profileData.phone_number.trim()) {
      toast.error('請填寫聯絡電話');
      return;
    }
    // Display name + phone are locked forever once created — confirm before committing.
    if (!editing && !window.confirm(`顯示名稱「${profileData.display_name.trim()}」同聯絡電話建立後將不可更改。\n確定用呢個名建立個人頁？`)) {
      return;
    }

    const price_range = `${profileData.price_currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;

    try {
      let response;
      if (editing) {
        response = await axios.put(
          `${API}/astrologer/profile`,
          {
            bio: profileData.bio,
            specialties,
            price_range,
            price_currency: profileData.price_currency,
            region: profileData.region,
            online_service: profileData.online_service,
            languages: profileData.languages,
            avatar: profileData.avatar || null,
          },
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${API}/astrologer/profile`,
          {
            display_name: profileData.display_name,
            bio: profileData.bio,
            specialties,
            price_range,
            price_currency: profileData.price_currency,
            region: profileData.region,
            online_service: profileData.online_service,
            languages: profileData.languages,
            phone_country_code: profileData.phone_country_code,
            phone_number: profileData.phone_number,
            avatar: profileData.avatar || null,
          },
          { withCredentials: true }
        );
      }
      setProfile(response.data);
      setShowProfileDialog(false);
      setEditing(false);
      refreshProfile();   // open the app-wide mandatory-profile gate
      toast.success(editing ? '個人頁已更新' : '個人頁已建立');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || (editing ? '更新失敗' : '建立失敗'));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Escape hatch from the mandatory-profile gate: a freshly-registered user who picked
  // the wrong role can switch (allowed only while the account has no profile/activity).
  const switchToClient = async () => {
    try {
      await axios.post(`${API}/auth/switch-role`, { role: 'client' }, { withCredentials: true });
      await checkAuth();
      navigate('/client/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '切換失敗');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">載入中...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
        <Link to="/astrologer/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <Popover open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#C93B2B] transition-colors"
                data-testid="profile-menu-trigger"
              >
                {profile?.avatar
                  ? <img src={profile.avatar} alt="" className="w-7 h-7 rounded-sm object-cover" />
                  : <span className="w-7 h-7 rounded-sm bg-[#C93B2B] text-white flex items-center justify-center"><User className="w-4 h-4" strokeWidth={1.5} /></span>}
                <span>{user?.name}</span>
                <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-sm p-0 max-h-[80vh] overflow-y-auto">
              {profile ? (
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-sm overflow-hidden bg-[#C93B2B] text-white flex items-center justify-center flex-shrink-0">
                      {profile.avatar
                        ? <img src={profile.avatar} alt="頭像" className="w-full h-full object-cover" />
                        : <User className="w-6 h-6" strokeWidth={1.5} />}
                    </div>
                    <div>
                      <p className="font-serif text-lg text-[#1A1A1A]">{profile.display_name}</p>
                      <p className="text-xs text-[#5C5C5C]">{profile.region} · {profile.online_service ? '支援線上服務' : '僅提供面談'}</p>
                    </div>
                  </div>
                  {profile.bio && <p className="text-sm text-[#5C5C5C] mb-3">{profile.bio}</p>}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {profile.specialties.map((s) => (
                      <span key={`spec-${s}`} className="border border-[#C93B2B] text-[#C93B2B] px-2 py-0.5 text-xs rounded-none font-serif">{s}</span>
                    ))}
                  </div>
                  <p className="text-xs text-[#5C5C5C] mb-1">{profile.price_range}</p>
                  {profile.languages && profile.languages.length > 0 && (
                    <p className="text-xs text-[#5C5C5C] mb-1">語言：{profile.languages.join('、')}</p>
                  )}
                  {profile.phone_number && (
                    <p className="text-xs text-[#5C5C5C] mb-3">
                      聯絡電話：{profile.phone_country_code} {profile.phone_number}
                      <span className="text-[#9A9A9A]">（只有你與管理員可見）</span>
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setProfileMenuOpen(false); openEdit(); }}
                    className="w-full rounded-sm border-[#C93B2B] text-[#C93B2B] hover:bg-[#F5F0E6]"
                    data-testid="edit-profile-btn"
                  >
                    編輯個人頁
                  </Button>
                </div>
              ) : (
                <div className="p-4">
                  <p className="text-sm text-[#5C5C5C] mb-3">尚未建立個人頁</p>
                  <Button
                    size="sm"
                    onClick={() => { setProfileMenuOpen(false); setEditing(false); setShowProfileDialog(true); }}
                    className="w-full rounded-sm bg-[#C93B2B] text-white hover:bg-[#A82E20]"
                    data-testid="create-profile-open-btn"
                  >
                    建立個人頁
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="rounded-sm"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            登出
          </Button>
        </div>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
          命理師工作台
        </h1>
        <p className="text-base text-[#5C5C5C] mb-8">
          瀏覽和回答求測者的求測單
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link to="/astrologer/requests">
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8 hover:shadow-md transition-shadow cursor-pointer h-full" data-testid="view-requests-card">
              <div className="w-12 h-12 bg-[#C93B2B] text-white rounded-sm flex items-center justify-center mb-4">
                <Plus className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">瀏覽求測單</h3>
              <p className="text-sm text-[#5C5C5C]">
                查看求測者公開的求測單，選擇感興趣的回答
              </p>
            </div>
          </Link>

          <Link to="/astrologer/answered">
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8 hover:shadow-md transition-shadow cursor-pointer h-full" data-testid="view-answered-card">
              <div className="w-12 h-12 bg-[#C93B2B] text-white rounded-sm flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">已回答求測單</h3>
              <p className="text-sm text-[#5C5C5C]">
                查看你回答過的求測單與結果{answeredCount > 0 ? `（${answeredCount}）` : ''}
              </p>
            </div>
          </Link>

          <Link to="/astrologer/orders">
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8 hover:shadow-md transition-shadow cursor-pointer h-full" data-testid="my-orders-summary-card">
              <div className="w-12 h-12 bg-[#C93B2B] text-white rounded-sm flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">我的訂單</h3>
              <p className="text-sm text-[#5C5C5C]">
                已成交 {orders.length} 單，進入諮詢室與求測者聯絡
              </p>
            </div>
          </Link>
        </div>
      </div>

      <Dialog open={showProfileDialog} onOpenChange={(open) => { if (!profile && !open) return; setShowProfileDialog(open); if (!open) setEditing(false); }}>
        <DialogContent
          className={`rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto ${!profile ? '[&>button]:hidden' : ''}`}
          onInteractOutside={(e) => { if (!profile) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (!profile) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? '編輯個人頁' : '建立個人頁'}</DialogTitle>
            <DialogDescription>
              {editing ? '更新你的個人頁資料（顯示名稱與聯絡電話不可更改）' : '開始使用前，請先完成個人頁設定（顯示名稱與聯絡電話建立後不可更改）'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProfile} className="space-y-4">
            <div>
              <Label>頭像（選填）</Label>
              <div className="flex items-center gap-4 mt-2">
                <div className="w-20 h-20 rounded-sm border border-[#E5E0D8] bg-[#F5F0E6] overflow-hidden flex items-center justify-center flex-shrink-0">
                  {profileData.avatar
                    ? <img src={profileData.avatar} alt="頭像預覽" className="w-full h-full object-cover" />
                    : <User className="w-8 h-8 text-[#C93B2B]" strokeWidth={1.5} />}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center px-3 py-1.5 rounded-sm border border-[#C93B2B] text-[#C93B2B] text-sm cursor-pointer hover:bg-[#F5F0E6]">
                    上載圖片
                    <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" data-testid="profile-avatar-input" />
                  </label>
                  {profileData.avatar && (
                    <button
                      type="button"
                      onClick={() => setProfileData((p) => ({ ...p, avatar: '' }))}
                      className="text-xs text-[#9A9A9A] hover:text-[#C93B2B] text-left"
                      data-testid="profile-avatar-remove"
                    >
                      移除頭像
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#9A9A9A] mt-1">付款前求測者只會見到代號，不會見到頭像</p>
            </div>

            <div>
              <Label htmlFor="display_name">顯示名稱 *</Label>
              <Input
                id="display_name"
                value={profileData.display_name}
                onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                placeholder="例：張大師"
                className="rounded-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                required
                disabled={editing}
                data-testid="profile-display-name"
              />
              <p className={`text-xs mt-1 ${editing ? 'text-[#9A9A9A]' : 'text-[#C93B2B]'}`}>顯示名稱建立後不可更改{editing ? '' : '，會公開畀所有求測者見到，請諗清楚'}</p>
            </div>

            <div>
              <Label htmlFor="bio">簡介 *</Label>
              <Textarea
                id="bio"
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                placeholder="介紹你的經歷和專長"
                className="rounded-sm mt-2"
                rows={4}
                required
                data-testid="profile-bio"
              />
            </div>

            <div>
              <Label>專長 *（可多選）</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SPECIALTY_OPTIONS.map((s) => {
                  const active = specialtiesSelected.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSpecialty(s)}
                      className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
                        active
                          ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                          : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                      }`}
                      data-testid={`profile-spec-${s}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <Input
                value={specialtiesCustom}
                onChange={(e) => setSpecialtiesCustom(e.target.value)}
                placeholder="其他專長（自行輸入，以逗號分隔）"
                className="rounded-sm mt-2"
                data-testid="profile-spec-custom"
              />
            </div>

            <div>
              <Label>價格範圍 *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Select value={profileData.price_currency} onValueChange={(v) => setProfileData({ ...profileData, price_currency: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="profile-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={profileData.price_min} onValueChange={(v) => setProfileData({ ...profileData, price_min: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="profile-price-min">
                    <SelectValue placeholder="下限" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_AMOUNTS.map((a) => (
                      <SelectItem key={a} value={String(a)}>{a.toLocaleString()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={profileData.price_max} onValueChange={(v) => setProfileData({ ...profileData, price_max: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="profile-price-max">
                    <SelectValue placeholder="上限" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_AMOUNTS.map((a) => (
                      <SelectItem key={a} value={String(a)}>{a.toLocaleString()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>地區 *</Label>
              <div className="mt-2">
                <CityPicker
                  value={profileData.region}
                  onChange={(v) => setProfileData({ ...profileData, region: v })}
                  testId="profile-region"
                />
              </div>
            </div>

            <div>
              <Label>提供語言 *（可多選）</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {LANGUAGE_OPTIONS.map((lang) => {
                  const active = profileData.languages.includes(lang);
                  return (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-2 rounded-sm border text-sm transition-colors ${
                        active
                          ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                          : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                      }`}
                      data-testid={`profile-lang-${lang}`}
                    >
                      {lang}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>聯絡電話 *</Label>
              <p className="text-xs text-[#5C5C5C] mt-1 mb-2">
                只供平台及管理員聯絡，不會向求測者公開；電話建立後不可更改
              </p>
              <div className="flex gap-2">
                <Select value={profileData.phone_country_code} onValueChange={(v) => setProfileData({ ...profileData, phone_country_code: v })} disabled={editing}>
                  <SelectTrigger className="rounded-sm w-[185px] flex-shrink-0 disabled:opacity-60" data-testid="profile-phone-code">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="flex items-center gap-2">
                          <img
                            src={`https://flagcdn.com/24x18/${c.iso}.png`}
                            srcSet={`https://flagcdn.com/48x36/${c.iso}.png 2x`}
                            width="20"
                            height="15"
                            alt=""
                            className="inline-block flex-shrink-0"
                          />
                          {c.name} {c.code}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  value={profileData.phone_number}
                  onChange={(e) => setProfileData({ ...profileData, phone_number: e.target.value })}
                  placeholder="電話號碼"
                  className="rounded-sm flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={editing}
                  data-testid="profile-phone-number"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
              data-testid="create-profile-btn"
            >
              {editing ? '儲存變更' : '建立個人頁'}
            </Button>
          </form>
          {!profile && (
            <div className="mt-1 pt-3 border-t border-[#E5E0D8] flex flex-wrap justify-between items-center gap-2 text-xs">
              <button type="button" onClick={() => navigate('/astrologer/requests')} className="text-[#C93B2B] hover:underline" data-testid="gate-browse-pool">
                想先睇下有咩求測單？隨便睇
              </button>
              <div className="flex items-center gap-4">
                <button type="button" onClick={switchToClient} className="text-[#5C5C5C] hover:underline" data-testid="switch-to-client">
                  改用求測者身份
                </button>
                <button type="button" onClick={handleLogout} className="text-[#5C5C5C] hover:underline" data-testid="gate-logout">
                  登出
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <footer className="px-6 md:px-12 lg:px-24 py-6 text-center text-xs text-[#9A9A9A]">
        <Link to="/privacy" className="hover:text-[#C93B2B] hover:underline">隱私政策</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="hover:text-[#C93B2B] hover:underline">服務條款</Link>
      </footer>
    </div>
  );
};

export default AstrologerDashboard;
